import { useEffect, useRef } from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import { FOCUS_RING, PILL_INK, PILL_OUTLINE, TEXT_INPUT, THEMES } from './constants';
import { ThemeButton } from './panels/ColorsPanel';
import { orderFields } from './fieldOrder';

// fixed lines such as "בשב\"ק פרשת {week}" fill themselves from the Day / Week answers
const SELF_FILLING = /\{(day|week)\}/;

const STEPS = ['Details', 'Colours', 'Preview'];

/**
 * Simple mode: one input per template line, colour themes, AI wording and a
 * preview button. Selecting a line on the canvas focuses its input.
 */
export const SimplePanel = ({
  fields,
  requiredIds = [],
  selectedId,
  focusTick = 0,
  onText,
  onFocusField,
  step,
  themeId,
  onApplyTheme,
  onAi,
  onPreview,
  onAdvanced,
  designPicker,
  tintControls,
  className,
}) => {
  const inputs = useRef({});

  useEffect(() => {
    // Deferred so it runs after the canvas click has finished moving focus.
    const timer = setTimeout(() => {
      const node = selectedId && inputs.current[selectedId];
      if (node && document.activeElement !== node) {
        node.focus({ preventScroll: true });
        node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [selectedId, focusTick]);

  const editable = orderFields(fields.filter((f) => !f.custom && !SELF_FILLING.test(f.text || '')));

  return (
    <div className={cn('flex flex-col gap-[18px]', className)} data-testid="simple-panel">
      <div className="flex flex-col gap-1.5">
        <p className="text-xs font-extrabold tracking-[0.12em] text-[#6E5B1C]">SIMPLE MODE</p>
        <h2 className="font-serif text-[26px] font-semibold leading-[1.1] text-ink">
          Fill in your details, <span className="italic font-medium">we do the design.</span>
        </h2>
      </div>

      <ol className="flex gap-2" aria-label="Progress">
        {STEPS.map((label, i) => {
          const done = i <= step;
          return (
            <li key={label} className="flex-1 flex flex-col gap-1.5" aria-current={i === step ? 'step' : undefined}>
              <span className={cn('h-1 rounded-sm', done ? 'bg-logo-gradient' : 'bg-[#EEEBE4]')} />
              <span className={cn('text-xs font-extrabold', done ? 'text-ink' : 'text-[#8C857A]')}>{label}</span>
            </li>
          );
        })}
      </ol>

      <div className="flex flex-col gap-3">
        {editable.length === 0 && <p className="text-sm text-[#6B665C]">This design has no editable lines.</p>}
        {editable.map((f, i) => {
          const multiline = (f.text || '').includes('\n') || (f.line_width && (f.text || '').length > 40);
          const common = {
            id: `simple-${f.id}`,
            ref: (node) => {
              inputs.current[f.id] = node;
            },
            dir: 'auto',
            value: f.text || '',
            maxLength: 2000,
            onChange: (e) => onText(f.id, e.target.value),
            onFocus: () => onFocusField(f.id),
            'data-testid': `simple-input-${f.id}`,
          };
          return (
            <div key={f.id} className="flex flex-col gap-1.5">
              <label htmlFor={`simple-${f.id}`} className="text-[13px] font-extrabold text-ink flex justify-between">
                {f.label || `Line ${i + 1}`}
                <span className="font-semibold text-[#6B665C]">
                  {requiredIds.includes(f.id) ? 'Required' : f.hidden && !f.options?.length ? 'Hidden' : ''}
                </span>
              </label>
              {Array.isArray(f.options) && f.options.length ? (
                <select
                  id={`simple-${f.id}`}
                  ref={common.ref}
                  dir="rtl"
                  value={f.text || ''}
                  onChange={common.onChange}
                  onFocus={common.onFocus}
                  className={cn(TEXT_INPUT, 'h-12 font-hebrew text-[18px] bg-white')}
                  data-testid={`simple-input-${f.id}`}
                >
                  {(f.options.includes(f.text) || !f.text ? f.options : [f.text, ...f.options]).map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              ) : multiline ? (
                <textarea {...common} rows={3} className={cn(TEXT_INPUT, 'py-2.5 font-hebrew text-[18px] leading-snug resize-y')} />
              ) : (
                <input {...common} className={cn(TEXT_INPUT, 'h-12 font-hebrew text-[18px]')} />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-col gap-2.5">
        <span className="text-[13px] font-extrabold text-ink">Colours</span>
        <div className="flex gap-2.5 flex-wrap">
          {THEMES.map((t) => (
            <ThemeButton key={t.id} theme={t} variant="dot" active={themeId === t.id} onApply={onApplyTheme} />
          ))}
        </div>
      </div>

      {tintControls}

      {designPicker}

      <button type="button" onClick={onAi} className={cn(PILL_OUTLINE, 'h-12 text-[15px] font-extrabold')} data-testid="simple-ai-btn">
        <Sparkles className="h-[18px] w-[18px] text-pink" aria-hidden="true" />
        Write the wording for me
      </button>

      <div className="mt-auto flex flex-col gap-2.5 pt-2">
        <button type="button" onClick={onPreview} className={cn(PILL_INK, 'h-[52px] text-base font-extrabold')} data-testid="simple-preview-btn">
          Looks good, preview it
        </button>
        <button
          type="button"
          onClick={onAdvanced}
          className={cn('h-10 text-sm font-bold text-[#4A4640] underline underline-offset-[3px] rounded-full hover:text-ink', FOCUS_RING)}
        >
          Want full control? Switch to Advanced
        </button>
      </div>
    </div>
  );
};

export default SimplePanel;
